/**
 * AutoTour — hands-free guided walkthrough of the QSR demo. A floating "Play
 * tour" button plays a branded intro slide, then steps through each module with
 * an on-screen caption and optional voiceover, so you can record a video or run
 * a self-presenting kiosk demo. Narration voice + speed are pickable.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Play, Pause, X, Clapperboard, Volume2, VolumeX, Settings2 } from 'lucide-react';

const STEPS = [
  { tab: 'overview', role: 'owner', title: 'Role-based home — Owner', body: 'One dashboard that reshapes for every role. This is the owner view: the whole network at a glance.' },
  { tab: 'overview', role: 'finance', title: 'Same home — Accountant', body: 'Switch role and the home re-skins instantly — a forensic finance view of labour, waste and margin.' },
  { tab: 'overview', role: 'store', title: 'Same home — Store Manager', body: 'And the store-manager view: their store, their actions, their roster — no clutter from other roles.' },
  { tab: 'locations', title: 'Multi-store leaderboard', body: 'Rank every store on SPLH & speed of service against benchmarks. Tap a store to drill into its full profile.' },
  { tab: 'labour', title: 'Labour & SPLH', body: 'Sales per labour hour with a day-part heat map — see exactly which shifts leak margin.' },
  { tab: 'speed', title: 'Speed of Service', body: 'Drive-through speed by day-part, benchmarked, with Corvana telling you where the bottleneck is.' },
  { tab: 'speedcoach', title: 'Live Speed Coach', body: 'Real-time pacing nudges to crew and auto-alerts to the manager when a store slips off pace.' },
  { tab: 'sales', title: 'Sales & Menu Mix', body: 'Average check, combo attachment and a day-part sales forecast so managers roster ahead of the peak.' },
  { tab: 'delivery', title: 'Delivery & Aggregators', body: 'Uber Eats + DoorDash mix, margins and demand by day-part — protect margin as delivery grows.' },
  { tab: 'waste', title: 'Waste & Voids', body: 'Waste and voids by store and reason, with the dollar margin at stake flagged by Corvana.' },
  { tab: 'insights', title: 'Corvana AI', body: 'Your morning playbook, assignable actions, manager digest and industry add-ons — priced per staff.' },
];

const INTRO = { title: 'The Daily Grill', subtitle: 'powered by Corvana', line: 'A guided tour of the full QSR operating picture — across every store and every role.' };
const OUTRO = { eyebrow: "That's the tour", title: "Thanks — let's talk", subtitle: 'The Daily Grill runs on Corvana', line: 'One place for every store, every role and the AI that runs the playbook.', cta: 'Book a walkthrough', url: 'https://corvana.com.au', email: 'hello@corvana.com.au' };

const STEP_MS = 5000;
const INTRO_MS = 4200;
const OUTRO_MS = 5200;
const IDLE_MS = 20000;

const SPEEDS = [
  { v: 0.85, label: 'Slow · 0.85×' },
  { v: 1, label: 'Normal · 1×' },
  { v: 1.1, label: 'Brisk · 1.1×' },
  { v: 1.25, label: 'Fast · 1.25×' },
];

export default function AutoTour({ onNavigate, onSetRole }) {
  const [playing, setPlaying] = useState(false);
  const [paused, setPaused] = useState(false);
  const [showIntro, setShowIntro] = useState(false);
  const [showOutro, setShowOutro] = useState(false);
  const [loop, setLoop] = useState(false);
  const [idx, setIdx] = useState(0);
  const [muted, setMuted] = useState(false);
  const [rate, setRate] = useState(1);
  const [voiceName, setVoiceName] = useState('');
  const [voices, setVoices] = useState([]);
  const [showSettings, setShowSettings] = useState(false);

  // Load available narration voices.
  useEffect(() => {
    if (typeof window === 'undefined' || !window.speechSynthesis) return undefined;
    const load = () => setVoices(window.speechSynthesis.getVoices());
    load();
    window.speechSynthesis.onvoiceschanged = load;
    return () => { window.speechSynthesis.onvoiceschanged = null; };
  }, []);

  const enVoices = useMemo(() => {
    const en = voices.filter((v) => /^en/i.test(v.lang));
    return en.length ? en : voices;
  }, [voices]);

  const cancelSpeech = () => { try { window.speechSynthesis && window.speechSynthesis.cancel(); } catch (e) { /* noop */ } };

  const speak = (text) => {
    if (muted || typeof window === 'undefined' || !window.speechSynthesis) return;
    try {
      const u = new SpeechSynthesisUtterance(text);
      u.rate = rate;
      const v = voices.find((x) => x.name === voiceName);
      if (v) u.voice = v;
      window.speechSynthesis.cancel();
      window.speechSynthesis.speak(u);
    } catch (e) { /* noop */ }
  };

  // Branded intro slide.
  useEffect(() => {
    if (!playing || !showIntro) return undefined;
    speak(`${INTRO.title}, ${INTRO.subtitle}. ${INTRO.line}`);
    const t = setTimeout(() => setShowIntro(false), INTRO_MS);
    return () => { clearTimeout(t); cancelSpeech(); };
  }, [playing, showIntro, muted, rate, voiceName, voices]);

  // Step progression + navigation.
  useEffect(() => {
    if (!playing || showIntro || showOutro) return undefined;
    onNavigate?.(STEPS[idx].tab);
    if (STEPS[idx].role) onSetRole?.(STEPS[idx].role);
    try { window.scrollTo({ top: 0, behavior: 'smooth' }); } catch (e) { /* noop */ }
    if (paused) return undefined;
    if (idx >= STEPS.length - 1) {
      const t = setTimeout(() => setShowOutro(true), STEP_MS);
      return () => clearTimeout(t);
    }
    const t = setTimeout(() => setIdx((i) => i + 1), STEP_MS);
    return () => clearTimeout(t);
  }, [playing, showIntro, showOutro, paused, idx, onNavigate, onSetRole]);

  // Step voiceover.
  useEffect(() => {
    if (!playing || showIntro || showOutro || paused) return undefined;
    const s = STEPS[idx];
    speak(`${s.title}. ${s.body}`);
    return () => cancelSpeech();
  }, [playing, showIntro, showOutro, paused, idx, muted, rate, voiceName, voices]);

  // Branded outro slide — loops for kiosk or ends the tour.
  useEffect(() => {
    if (!playing || !showOutro) return undefined;
    speak(`${OUTRO.title}. ${OUTRO.subtitle}. ${OUTRO.line}`);
    const t = setTimeout(() => {
      if (loop) { setShowOutro(false); setIdx(0); setShowIntro(true); }
      else { setPlaying(false); setShowOutro(false); setIdx(0); onNavigate?.('overview'); }
    }, OUTRO_MS);
    return () => { clearTimeout(t); cancelSpeech(); };
  }, [playing, showOutro, loop, muted, rate, voiceName, voices]);

  const start = () => { setShowSettings(false); setPaused(false); setShowOutro(false); setShowIntro(true); setIdx(0); setPlaying(true); };
  const stop = () => { cancelSpeech(); setPlaying(false); setPaused(false); setShowIntro(false); setShowOutro(false); setIdx(0); onNavigate?.('overview'); };
  const replay = () => { cancelSpeech(); setShowOutro(false); setIdx(0); setShowIntro(true); };

  // Kiosk idle-start — when looping and idle, auto-play after inactivity.
  useEffect(() => {
    if (!loop || playing || typeof window === 'undefined') return undefined;
    let timer;
    const arm = () => { clearTimeout(timer); timer = setTimeout(() => start(), IDLE_MS); };
    const events = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll', 'wheel'];
    events.forEach((e) => window.addEventListener(e, arm, { passive: true }));
    arm();
    return () => { clearTimeout(timer); events.forEach((e) => window.removeEventListener(e, arm)); };
  }, [loop, playing]);

  // Broadcast kiosk-loop state so the live order feed runs alongside the tour.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.dispatchEvent(new CustomEvent('qsr-kiosk-loop', { detail: { loop } }));
  }, [loop]);

  const togglePlay = () => { cancelSpeech(); setPaused((p) => !p); };
  const toggleMute = () => { setMuted((m) => { if (!m) cancelSpeech(); return !m; }); };

  const renderSettings = (align = 'right') => (
    <div className={`fixed left-4 right-4 bottom-24 w-auto sm:absolute sm:bottom-full sm:right-auto sm:mb-2 ${align === 'left' ? 'sm:left-0' : 'sm:left-auto sm:right-0'} sm:w-64 z-50 rounded-xl bg-slate-900 text-white ring-1 ring-white/10 shadow-2xl p-3 space-y-3`} data-testid="autotour-settings-panel">
      <div>
        <label className="text-[11px] uppercase tracking-wide text-slate-400">Narration voice</label>
        <select
          data-testid="autotour-voice-select"
          value={voiceName}
          onChange={(e) => setVoiceName(e.target.value)}
          className="mt-1 w-full rounded-md bg-slate-800 text-white text-xs px-2 py-1.5 outline-none"
        >
          <option value="">System default</option>
          {enVoices.map((v) => <option key={v.name} value={v.name}>{v.name}</option>)}
        </select>
      </div>
      <div>
        <label className="text-[11px] uppercase tracking-wide text-slate-400">Speed</label>
        <select
          data-testid="autotour-speed-select"
          value={rate}
          onChange={(e) => setRate(Number(e.target.value))}
          className="mt-1 w-full rounded-md bg-slate-800 text-white text-xs px-2 py-1.5 outline-none"
        >
          {SPEEDS.map((s) => <option key={s.v} value={s.v}>{s.label}</option>)}
        </select>
      </div>
      <label className="flex items-center justify-between gap-2 pt-1 cursor-pointer" data-testid="autotour-loop-toggle">
        <span className="text-xs font-medium text-slate-200">Loop for kiosk</span>
        <input
          type="checkbox"
          checked={loop}
          onChange={(e) => setLoop(e.target.checked)}
          className="h-4 w-4 accent-emerald-500"
          data-testid="autotour-loop-checkbox"
        />
      </label>
    </div>
  );

  if (!playing) {
    return (
      <div className="fixed bottom-6 left-6 z-50 flex items-center gap-2">
        <button
          onClick={start}
          className="inline-flex items-center gap-2 rounded-full bg-slate-900 text-white pl-4 pr-5 py-3 text-sm font-semibold shadow-2xl ring-1 ring-white/10 hover:bg-slate-800 transition-colors"
          data-testid="autotour-play"
        >
          <Clapperboard className="w-4 h-4 text-emerald-400" /> Watch demo tour
        </button>
        <div className="relative">
          <button
            onClick={() => setShowSettings((s) => !s)}
            className="p-3 rounded-full bg-slate-900 text-white shadow-2xl ring-1 ring-white/10 hover:bg-slate-800 transition-colors"
            data-testid="autotour-settings"
            title="Narration settings"
          >
            <Settings2 className="w-4 h-4" />
          </button>
          {showSettings && renderSettings('left')}
        </div>
      </div>
    );
  }

  // Branded intro slide.
  if (showIntro) {
    return (
      <div className="fixed inset-0 z-50 grid place-items-center bg-slate-950/80 backdrop-blur-md" data-testid="autotour-intro">
        <div className="text-center px-6">
          <div className="inline-flex items-center gap-2 text-emerald-400 text-xs font-semibold uppercase tracking-[0.2em] mb-4">
            <Clapperboard className="w-4 h-4" /> Guided tour
          </div>
          <h1 className="text-white text-5xl sm:text-6xl font-black tracking-tight">{INTRO.title}</h1>
          <p className="mt-2 text-lg text-slate-300">{INTRO.subtitle}</p>
          <p className="mt-5 max-w-lg mx-auto text-sm text-slate-400 leading-relaxed">{INTRO.line}</p>
          <div className="mt-8 flex items-center justify-center gap-3">
            <button onClick={() => setShowIntro(false)} className="rounded-full bg-white/10 hover:bg-white/20 text-white text-sm font-semibold px-5 py-2.5 transition-colors" data-testid="autotour-intro-skip">Start now</button>
            <button onClick={stop} className="rounded-full text-slate-400 hover:text-white text-sm px-4 py-2.5 transition-colors" data-testid="autotour-intro-cancel">Cancel</button>
          </div>
        </div>
      </div>
    );
  }

  // Branded outro slide.
  if (showOutro) {
    return (
      <div className="fixed inset-0 z-50 grid place-items-center bg-slate-950/85 backdrop-blur-md" data-testid="autotour-outro">
        <div className="text-center px-6">
          <div className="text-emerald-400 text-xs font-semibold uppercase tracking-[0.2em] mb-4">{OUTRO.eyebrow}</div>
          <h1 className="text-white text-5xl sm:text-6xl font-black tracking-tight">{OUTRO.title}</h1>
          <p className="mt-3 text-lg text-slate-300">{OUTRO.subtitle}</p>
          <p className="mt-4 max-w-lg mx-auto text-sm text-slate-400 leading-relaxed">{OUTRO.line}</p>
          <div className="mt-6 flex flex-col items-center gap-2">
            <a
              href={OUTRO.url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-full bg-emerald-500 hover:bg-emerald-400 text-slate-950 text-sm font-semibold px-6 py-2.5 transition-colors"
              data-testid="autotour-outro-cta"
            >
              {OUTRO.cta}
            </a>
            <a href={`mailto:${OUTRO.email}`} className="text-xs text-slate-400 hover:text-emerald-300 transition-colors" data-testid="autotour-outro-email">{OUTRO.email}</a>
          </div>
          <div className="mt-8 flex items-center justify-center gap-3">
            <button onClick={replay} className="rounded-full bg-white/10 hover:bg-white/20 text-white text-sm font-semibold px-5 py-2.5 transition-colors" data-testid="autotour-outro-replay">Replay tour</button>
            <button onClick={stop} className="rounded-full text-slate-400 hover:text-white text-sm px-4 py-2.5 transition-colors" data-testid="autotour-outro-done">Done</button>
          </div>
        </div>
      </div>
    );
  }

  const step = STEPS[idx];
  return (
    <div className="fixed inset-x-0 bottom-0 z-50 flex justify-center px-4 pb-6 pointer-events-none" data-testid="autotour-caption">
      <div className="pointer-events-auto relative w-full max-w-2xl rounded-2xl bg-slate-900/90 text-white backdrop-blur-xl ring-1 ring-white/10 shadow-2xl p-4 sm:p-5">
        <div className="flex items-start gap-3">
          <div className="shrink-0 mt-0.5 w-9 h-9 rounded-full bg-emerald-500/20 text-emerald-300 grid place-items-center text-sm font-bold">{idx + 1}</div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold">{step.title}</div>
            <p className="text-xs text-slate-300 mt-0.5 leading-snug">{step.body}</p>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <div className="relative">
              <button onClick={() => setShowSettings((s) => !s)} className="p-2 rounded-lg hover:bg-white/10" data-testid="autotour-settings" title="Narration settings">
                <Settings2 className="w-4 h-4" />
              </button>
              {showSettings && renderSettings()}
            </div>
            <button onClick={toggleMute} className="p-2 rounded-lg hover:bg-white/10" data-testid="autotour-mute" title={muted ? 'Unmute narration' : 'Mute narration'}>
              {muted ? <VolumeX className="w-4 h-4 text-slate-400" /> : <Volume2 className="w-4 h-4 text-emerald-300" />}
            </button>
            <button onClick={togglePlay} className="p-2 rounded-lg hover:bg-white/10" data-testid="autotour-pause" title={paused ? 'Resume' : 'Pause'}>
              {paused ? <Play className="w-4 h-4" /> : <Pause className="w-4 h-4" />}
            </button>
            <button onClick={stop} className="p-2 rounded-lg hover:bg-white/10" data-testid="autotour-stop" title="End tour"><X className="w-4 h-4" /></button>
          </div>
        </div>
        <div className="mt-3 flex items-center gap-1.5">
          {STEPS.map((s, i) => (
            <button
              key={i}
              onClick={() => setIdx(i)}
              className={`h-1.5 rounded-full transition-all ${i === idx ? 'w-6 bg-emerald-400' : 'w-1.5 bg-white/25 hover:bg-white/40'}`}
              data-testid={`autotour-dot-${i}`}
              aria-label={s.title}
            />
          ))}
          <span className="ml-auto text-[11px] text-slate-400">{idx + 1} / {STEPS.length}</span>
        </div>
      </div>
    </div>
  );
}
